import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../../components/PageHero';
import {
  IconWind, IconDroplet, IconLeaf, IconGlobe, IconFileText,
  IconAlertTri, IconShieldCheck, IconCheckCircle, IconTarget,
  IconBarChart, IconCpu, IconActivity, IconLayers,
} from '../../components/Icons';
import '../nosotros/Nosotros.css';
import './Software.css';

const MODULES = [
  {
    Icon: IconWind,
    color: 'blue',
    title: 'Calidad del Aire y Emisiones',
    items: [
      'Inventario de emisiones atmosféricas (EPA AP-42)',
      'Material particulado PM10 y PM2.5',
      'Modelación de dispersión de contaminantes',
      'Monitoreo de ruido ambiental (ISO 9613-2)',
    ],
  },
  {
    Icon: IconDroplet,
    color: 'green',
    title: 'Recurso Hídrico',
    items: [
      'Balance hídrico Thornthwaite-Mather',
      'Caudal ecológico y caudales extremos',
      'Índice de calidad de agua (ICA)',
      'Análisis de vertimientos (Res. 631/2015)',
    ],
  },
  {
    Icon: IconLeaf,
    color: 'blue',
    title: 'Biodiversidad y Ecosistemas',
    items: [
      'Índices de diversidad: Shannon, Simpson, Margalef',
      'Inventario forestal (volumen, biomasa, carbono)',
      'Cálculo de compensación ambiental (ANLA)',
      'Inventario y monitoreo de especies',
    ],
  },
  {
    Icon: IconGlobe,
    color: 'green',
    title: 'Suelos y Paisaje',
    items: [
      'USLE — Ecuación Universal de Pérdida de Suelo',
      'Estabilidad de taludes y susceptibilidad',
      'Calidad visual intrínseca (CVI), exposición (EV)',
      'Capacidad de absorción visual (CAV, FV)',
    ],
  },
  {
    Icon: IconFileText,
    color: 'blue',
    title: 'Gestión de Impactos Ambientales',
    items: [
      'Matriz Conesa 1997: significancia de impactos',
      'Evaluación de riesgos ambientales',
      'Compensación ambiental cuantificada',
      'Análisis socioeconómico de área de influencia',
    ],
  },
  {
    Icon: IconAlertTri,
    color: 'green',
    title: 'Huella de Carbono y Energía',
    items: [
      'Inventario GEI: Scopes 1, 2 y 3 (GHG Protocol)',
      'Metodología ISO 14064-1:2018 e IPCC Tier 1',
      'Consumo energético e IEE (UPME 2023)',
      'Cumplimiento Ley 1931/2018 cambio climático',
    ],
  },
  {
    Icon: IconShieldCheck,
    color: 'blue',
    title: 'Residuos y Sustancias',
    items: [
      'Gestión y clasificación de residuos peligrosos',
      'Residuos sólidos municipales y reciclaje',
      'Control de descapote y manejo de estériles',
      'Cargas contaminantes y balance de masa',
    ],
  },
  {
    Icon: IconLayers,
    color: 'green',
    title: 'Vulnerabilidad e Hidrogeología',
    items: [
      'Vulnerabilidad GOD de acuíferos',
      'Balance hidrológico y recarga de agua subterránea',
      'Modelación de interacciones agua superficial/subterránea',
      'Índice de escasez hídrica (Decreto 1076/2015)',
    ],
  },
  {
    Icon: IconBarChart,
    color: 'blue',
    title: 'Reportes para Autoridades Ambientales',
    items: [
      'ICAS para CORPOBOYACÁ, CAR, ANLA',
      'Reportes de Planes de Manejo Ambiental (PMA)',
      'Exportación automática a Excel, PDF y Word',
      'Trazabilidad de monitoreos y cumplimiento',
    ],
  },
];

const HIGHLIGHTS = [
  {
    Icon: IconTarget,
    color: 'green',
    title: 'Normativa colombiana integrada',
    desc: 'Decreto 1076/2015, Res. 2254/2017, Res. 631/2015, Res. 627/2006, Res. 909/2008, Decreto 2041/2014, Ley 99/1993, Ley 1931/2018 y Manual MADS 2018.',
  },
  {
    Icon: IconShieldCheck,
    color: 'blue',
    title: '30 módulos ambientales',
    desc: 'Cubre desde calidad del aire y recurso hídrico hasta huella de carbono, biodiversidad y gestión de residuos.',
  },
  {
    Icon: IconActivity,
    color: 'green',
    title: 'Monitoreo y semáforos',
    desc: 'Visualización de cumplimiento en tiempo real con gráficas de semáforo, radar y tendencias históricas.',
  },
  {
    Icon: IconCpu,
    color: 'blue',
    title: 'Metodologías científicas',
    desc: 'Chave 2014 para biomasa, CONIF 2002 para inventario forestal, Escribano 1991 para paisaje, GHG Protocol para GEI.',
  },
];


export default function SoftwareAmbiental() {
  return (
    <div>
      <PageHero
        title="Software Ambiental"
        subtitle="Plataforma integral de gestión ambiental: calidad del aire, recurso hídrico, biodiversidad, huella de carbono y cumplimiento normativo."
        variant="blue"
      />

      {/* ── Overview ── */}
      <section className="section">
        <div className="container">
          <div className="qs-grid">
            <div className="qs-text anim-fade-up">
              <div className="divider divider--blue" />
              <h2 className="section-title">Sistema Integral de Gestión Ambiental</h2>
              <p>
                El módulo ambiental de nuestra plataforma es una solución profesional de escritorio
                diseñada por <strong>Impacta M.A. SAS</strong> para gestionar todos los componentes
                ambientales que exige un proyecto minero o de infraestructura en Colombia. Centraliza
                en un único sistema los cálculos, el seguimiento de indicadores y la generación de
                informes para presentación ante el <strong>ANLA</strong>,{' '}
                <strong>CORPOBOYACÁ</strong> y demás corporaciones autónomas regionales.
              </p>
              <p>
                Incorpora más de 30 módulos especializados que cubren la totalidad de los
                componentes ambientales: atmósfera, agua, suelo, biota, paisaje, energía, residuos
                y gestión socioeconómica. Cada módulo ejecuta los cálculos según la normativa
                vigente: <strong>Decreto 1076/2015</strong> (Código de Recursos Naturales),{' '}
                <strong>Resolución 2254/2017</strong> (calidad del aire),{' '}
                <strong>Resolución 627/2006</strong> (ruido ambiental),{' '}
                <strong>Resolución 631/2015</strong> (vertimientos),{' '}
                <strong>Resolución 909/2008</strong> (emisiones atmosféricas),{' '}
                <strong>Decreto 2041/2014</strong> (licencias ambientales),{' '}
                <strong>Ley 99/1993</strong>, <strong>Ley 1931/2018</strong> (cambio climático),{' '}
                <strong>Resolución 256/2018 ANLA</strong> (compensación ambiental) y metodologías
                internacionales —Thornthwaite-Mather, GHG Protocol, ISO 14064-1, Conesa 1997—
                generando automáticamente los informes técnicos requeridos.
              </p>
              <p>
                El sistema permite el seguimiento en tiempo real del cumplimiento de las
                obligaciones del Plan de Manejo Ambiental (PMA), con semáforos de alerta y
                trazabilidad completa de todos los monitoreos y valores registrados.
              </p>
            </div>
            <div className="qs-card anim-fade-up anim-delay-2">
              <div className="qs-card__logo-wrap">
                <picture>
                  <source srcSet="/logo3.webp" type="image/webp" />
                  <img src="/logo3.webp" alt="Impacta M.A. SAS" />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Modules ── */}
      <section className="section section--bg">
        <div className="container">
          <div className="divider divider--blue" />
          <h2 className="section-title">Módulos del Sistema</h2>
          <p className="section-lead" style={{ marginBottom: '40px' }}>
            Cada módulo realiza el cálculo técnico, registra los datos de monitoreo y genera
            el reporte listo para presentación ante la autoridad ambiental competente.
          </p>
          <div className="sw-modules-grid">
            {MODULES.map(({ Icon, color, title, items }) => (
              <div key={title} className="sw-module-card">
                <div className={`sw-module-card__icon sw-module-card__icon--${color}`}>
                  <Icon size={26} />
                </div>
                <h3>{title}</h3>
                <ul>
                  {items.map(item => (
                    <li key={item}>
                      <IconCheckCircle
                        size={14}
                        className="sw-check"
                        style={{ color: color === 'green' ? 'var(--c-green-500)' : 'var(--c-blue-500)' }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Highlights ── */}
      <section className="section">
        <div className="container">
          <div className="divider divider--blue divider--center" />
          <h2 className="section-title section-title--center">Ventajas del sistema</h2>
          <div className="sw-highlights" style={{ marginTop: '40px' }}>
            {HIGHLIGHTS.map(({ Icon, color, title, desc }) => (
              <div key={title} className="sw-highlight card card--elevated">
                <div className={`sw-highlight__icon sw-highlight__icon--${color}`}>
                  <Icon size={26} />
                </div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section section--dark">
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="divider divider--center" style={{ background: 'var(--c-blue-400)' }} />
          <h2 className="section-title section-title--white" style={{ marginBottom: '12px' }}>
            ¿Desea conocer el software ambiental?
          </h2>
          <p className="section-lead section-lead--center section-lead--white" style={{ marginBottom: '32px' }}>
            Solicite una demostración o consulte sobre la implementación del sistema en su proyecto minero o ambiental.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contacto" className="btn btn--blue btn--lg">Solicitar Demostración</Link>
            <Link to="/software/minero" className="btn btn--ghost-white btn--lg">Ver Software Minero</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
